import { ReactNode, useState } from "react";
import { Menu, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "../ui/button";
import { SiteLink } from "./SiteLink";
import { ThemeToggle } from "./ThemeToggle";

export interface NavLink {
  href: string;
  label: string;
  external?: boolean;
}

export interface SiteNavbarProps {
  logo: ReactNode;
  navLinks: NavLink[];
  rightContent?: ReactNode;
  variant?: "homepage" | "standard";
  position?: "sticky" | "fixed";
  sidebarToggle?: ReactNode;
  className?: string;
}

export function SiteNavbar({
  logo,
  navLinks,
  rightContent,
  variant = "standard",
  position = "sticky",
  sidebarToggle,
  className,
}: SiteNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const bgClasses =
    variant === "homepage"
      ? "bg-white/80 dark:bg-black/80"
      : "bg-white/80 dark:bg-zinc-950/80";

  const positionClasses =
    position === "fixed" ? "fixed inset-x-0 top-0" : "sticky top-0";

  return (
    <nav
      className={cn(
        positionClasses,
        "z-50 backdrop-blur-lg border-b border-zinc-200 dark:border-zinc-800",
        bgClasses,
        className,
      )}
    >
      <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          {sidebarToggle}

          <a href="/" className="shrink-0">
            {logo}
          </a>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.external ? (
                <Button
                  key={link.href}
                  asChild
                  variant="ghost"
                  className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white transition-none"
                >
                  <a href={link.href} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                </Button>
              ) : (
                <Button
                  key={link.href}
                  asChild
                  variant="ghost"
                  className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white transition-none"
                >
                  <SiteLink href={link.href}>{link.label}</SiteLink>
                </Button>
              ),
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center">
          {rightContent ?? <ThemeToggle />}
        </div>

        <div className="flex md:hidden items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-zinc-600 dark:text-zinc-400"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-zinc-200 dark:border-zinc-800">
          <div className="mx-auto max-w-7xl px-6 py-4 space-y-4">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ) : (
                  <SiteLink
                    key={link.href}
                    href={link.href}
                    className="text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-black dark:hover:text-white py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </SiteLink>
                ),
              )}
            </div>
            <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
              {rightContent ?? <ThemeToggle />}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
